import { put, takeLatest, call, takeEvery } from 'redux-saga/effects'
import { programSuccessActions } from './ProgramSuccessActions'
import { programSuccessReducers } from './ProgramSuccessSlice'
import { ActionCreator } from '@reduxjs/toolkit'
import SegmentService from 'redux/config/services/SegmentService'
import moment from 'moment'
import { flexActions } from 'redux/flex/flexSlice'
import { transitPassActions } from 'redux/transitPass/transitPassActions'
import { grhActions } from 'redux/grh/grhSlice'
import { micromobilityActions } from 'redux/micromobility/micromobilitySlice'
import { qtfActions } from 'redux/qtf/qtfSlice'
import qtfService from 'redux/qtf/qtfService'
import grhService from 'redux/grh/grhService'
import micromobilityService from 'redux/micromobility/micromobilityService'
import flexService from 'redux/flex/flexService'
import { getProgramScopePayload } from '../Shared/helperFunctions'

function* newEditCycleCleanupSaga() {
  yield put(programSuccessReducers.setNewEditCycleCleanup())
}

const createMerchants = (activeMerchantIds: string[], activeOptions: any) => {
  try {
    const merchants = []
    Object.keys(activeOptions).forEach((key) => {
      if (activeOptions[key] && activeMerchantIds[key]) {
        merchants.push(...activeMerchantIds[key])
      }
    })
    return merchants
  } catch (error) {
    return []
  }
}

const createMerchantsName = (merchants: string[], merchantsMap: any) => {
  try {
    return merchants.map((m) => merchantsMap[m]?.name)
  } catch (error) {
    return []
  }
}

function* ProgramSuccessfulFlow(
  response: any,
  segmentObject: any,
  segmentId: string,
  programType: string,
  successAction: ActionCreator<any>,
  programObject: any = {}
) {
  if (response.status == 201 || response.status == 200) {
    yield put(
      programSuccessReducers.addSuccessfulEditProgramIds({
        type: programType,
        id: segmentId,
      })
    )

    yield put({
      type: successAction().type,
      payload: response.data,
      meta: programObject,
    })

    SegmentService.track('programs-action-click', segmentObject)
  } else {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: programType,
        id: segmentId,
      })
    )
  }
}

function* programCancelFlow(
  response: any,
  successAction: ActionCreator<any>,
  programId: string,
  programType: string
) {
  if (response.status == 204) {
    yield put({
      type: successAction().type,
      payload: response.data,
      meta: programId,
    })
  }
  SegmentService.track('programs-cancelled', {
    programType,
    programId,
  })
  // Only adding errors not success, because it will go over the selected segments
  if (response.status != 204) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: programType,
        id: programId,
      })
    )
  }
}

/*
########################################################
###################### QTF ##############################
########################################################
*/

function* createBulkQtfProgramsForSegmentListSaga(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>
) {
  const {
    qtf,
    selectedDate,
    segmentIdList,
    currentUserId,
    launchId,
    programId,
    typeMap,
    superProgramName,
    description,
  } = action.payload
  try {
    const taxContributionTypes = []
    if (qtf.benefits.transit) taxContributionTypes.push('COMMUTER_TRANSIT')
    if (qtf.benefits.parking) taxContributionTypes.push('COMMUTER_PARKING')

    const programObject = {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      segmentList: segmentIdList,
      types: taxContributionTypes,
      contributionDeadlineDay: qtf.finalizeAllocation,
      manual: true,
      nextValidClose: moment(selectedDate.iso).format('YYYY-MM-DD'),
      payrollCycle: qtf.payrollSchedule,
      commUserIds: [currentUserId],
      employerTransitContribution: qtf.employerContributions.transit,
      employerParkingContribution: qtf.employerContributions.parking,
      employerTransitMaxAllocationLimit: qtf.maxAllocationLimit.transit,
      employerParkingMaxAllocationLimit: qtf.maxAllocationLimit.parking,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(typeMap && { typeMap }),
    }

    // createBulkQtfPrograms returns an array of promises, so we need to handle them with Promise.all
    const responses = yield call(createUpdateService, programObject)

    // Handle success for bulk creation - mark all segments as successful
    if (
      responses &&
      responses.length > 0 &&
      (responses[0].status === 201 || responses[0].status === 200)
    ) {
      // Mark all segments as successfully created
      for (const segmentId of segmentIdList) {
        yield put(
          programSuccessReducers.addSuccessfulEditProgramIds({
            type: 'qtf',
            id: segmentId,
          })
        )
      }

      // Dispatch success action for all segments
      yield put({
        type: successAction().type,
        payload: responses[0].data,
        meta: programObject,
      })

      // Track analytics for bulk creation
      SegmentService.track('programs-action-click', {
        action: 'activate',
        programType: 'qtf',
        segmentCount: segmentIdList.length,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        types: taxContributionTypes,
        contributionDeadlineDay: qtf.finalizeAllocation,
        manual: false,
        nextValidClose: null,
        payrollCycle: null,
      })
    } else {
      // Handle error for all segments
      for (const segmentId of segmentIdList) {
        yield put(
          programSuccessReducers.addProgramEditError({
            type: 'qtf',
            id: segmentId,
          })
        )
      }
    }
  } catch (error) {
    // Handle error for all segments
    for (const segmentId of segmentIdList) {
      yield put(
        programSuccessReducers.addProgramEditError({
          type: 'qtf',
          id: segmentId,
        })
      )
    }
  }
}

function* createUpdateQtfProgramForASegmentSaga(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    qtf,
    selectedDate,
    segment,
    currentUserId,
    launchId,
    programId,
    typeMap,
    superProgramName,
    description,
  } = action.payload
  try {
    const taxContributionTypes = []
    if (qtf.benefits.transit) taxContributionTypes.push('COMMUTER_TRANSIT')
    if (qtf.benefits.parking) taxContributionTypes.push('COMMUTER_PARKING')

    const programObject = {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      types: taxContributionTypes,
      contributionDeadlineDay: qtf.finalizeAllocation,
      manual: true,
      nextValidClose: moment(selectedDate.iso).format('YYYY-MM-DD'),
      payrollCycle: qtf.payrollSchedule,
      commUserIds: [currentUserId],
      employerTransitContribution: qtf.employerContributions.transit,
      employerParkingContribution: qtf.employerContributions.parking,
      employerTransitMaxAllocationLimit: qtf.maxAllocationLimit.transit,
      employerParkingMaxAllocationLimit: qtf.maxAllocationLimit.parking,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(typeMap && { typeMap }),
    }
    const responses = yield call(createUpdateService, programObject)

    // There will be multiple responses because we make separate requests for each type
    // Assuming even one call is 201, we will have success on the UI
    yield ProgramSuccessfulFlow(
      responses[0],
      {
        action: actionType,
        programType: 'qtf',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        types: taxContributionTypes,
        contributionDeadlineDay: qtf.finalizeAllocation,
        manual: false,
        nextValidClose: null,
        payrollCycle: null,
      },
      segment.id,
      'qtf',
      successAction,
      programObject
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'qtf',
        id: segment.id,
      })
    )
  }
}

function* createQtfProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateQtfProgramForASegmentSaga,
    action,
    qtfService.createQtfProgram,
    qtfActions.createQtfProgramSuccess,
    'activate'
  )
}

function* createBulkQtfProgramsSaga(action: any) {
  yield call(
    createBulkQtfProgramsForSegmentListSaga,
    action,
    qtfService.createBulkQtfPrograms,
    qtfActions.createBulkQtfProgramsSuccess
  )
}

function* updateQtfProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateQtfProgramForASegmentSaga,
    action,
    qtfService.updateQtfProgram,
    qtfActions.updateQtfProgramSuccess,
    'update'
  )
}

function* cancelQtfProgramForASegmentSaga(action: any) {
  const { programIds } = action.payload
  try {
    const responses = yield call(qtfService.cancelQtfProgram, programIds)
    yield programCancelFlow(
      responses[0],
      qtfActions.cancelQtfProgramSuccess,
      programIds[0],
      'qtf'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'qtf',
        id: programIds[0],
      })
    )
  }
}

/*
########################################################
###################### GRH ##############################
########################################################
*/
function* createUpdateGrhProgramForASegmentSaga(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    grh,
    selectedDate,
    segment,
    merchantsMap,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const merchants = createMerchants(grh.activeMerchantIds, grh.activeOptions)
    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'grh',
      type: 'GRH',
      budget: grh.transaction,
      numUses: grh.uses,
      requiresRequest: true,
      merchants: merchants,
      budgetCycle: 'YEARLY',
      governmentGrh: grh.activeOptions.governmentGrh,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'grh',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        budget: grh.transaction,
        flexProviders: createMerchantsName(
          grh.activeMerchantIds.flex,
          merchantsMap
        ),
        gasProviders: createMerchantsName(
          grh.activeMerchantIds.gas,
          merchantsMap
        ),
        numUses: grh.uses,
        localTaxi: grh.activeOptions.localTaxi,
        budgetCycle: 'YEARLY',
        governmentGrh: grh.activeOptions.governmentGrh,
      },
      segment.id,
      'grh',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'grh',
        id: segment.id,
      })
    )
  }
}

function* createGrhProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateGrhProgramForASegmentSaga,
    action,
    grhService.createGrhProgram,
    grhActions.createGrhProgramSuccess,
    'activate'
  )
}

function* updateGrhProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateGrhProgramForASegmentSaga,
    action,
    grhService.updateGrhProgram,
    grhActions.updateGrhProgramSuccess,
    'update'
  )
}

function* cancelGrhProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(grhService.cancelGrhProgram, programId)
    yield programCancelFlow(
      response,
      grhActions.cancelGrhProgramSuccess,
      programId,
      'grh'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'grh',
        id: programId,
      })
    )
  }
}

/*
########################################################
###################### MICROMOBILITY ######################
########################################################
*/
function* createUpdateMicromobilityProgramForASegmentSaga(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    micromobility,
    selectedDate,
    segment,
    merchantsMap,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const merchants = createMerchants(
      micromobility.activeMerchantIds,
      micromobility.activeOptions
    )

    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'micromobility',
      type: 'MICROMOBILITY',
      budget: micromobility.budget,
      merchants: merchants,
      prorated: micromobility.prorated ?? false,
      budgetCycle: micromobility.activeOptions.oneTime ? 'YEARLY' : 'MONTHLY',
      leasing: micromobility.activeOptions.leasing,
      shared: micromobility.activeOptions.shared,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'micromobility',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        budget: micromobility.budget,
        budgetCycle: micromobility.activeOptions.oneTime ? 'YEARLY' : 'MONTHLY',
        oneTime: createMerchantsName(
          micromobility.activeMerchantIds.oneTime,
          merchantsMap
        ),
        shared: createMerchantsName(
          micromobility.activeMerchantIds.shared,
          merchantsMap
        ),
        leasing: createMerchantsName(
          micromobility.activeMerchantIds.leasing,
          merchantsMap
        ),
      },
      segment.id,
      'micromobility',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'micromobility',
        id: segment.id,
      })
    )
  }
}

function* createMicromobilityProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateMicromobilityProgramForASegmentSaga,
    action,
    micromobilityService.createMicromobilityProgram,
    micromobilityActions.createMicromobilityProgramSuccess,
    'activate'
  )
}

function* updateMicromobilityProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateMicromobilityProgramForASegmentSaga,
    action,
    micromobilityService.updateMicromobilityProgram,
    micromobilityActions.updateMicromobilityProgramSuccess,
    'update'
  )
}

function* cancelMicromobilityProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(
      micromobilityService.cancelMicromobilityProgram,
      programId
    )
    yield programCancelFlow(
      response,
      micromobilityActions.cancelMicromobilityProgramSuccess,
      programId,
      'micromobility'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'micromobility',
        id: programId,
      })
    )
  }
}

/*
########################################################
###################### FLEX #############################
########################################################
*/
function* createUpdateFlexProgramForASegment(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    flex,
    selectedDate,
    segment,
    merchantsMap,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const merchants = createMerchants(
      flex.activeMerchantIds,
      flex.activeOptions
    )
    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'flex',
      type: 'FLEX',
      budget: flex.budget,
      budgetCycle: flex.budgetCycle,
      oneTimePurchase: flex.oneTimePurchase ?? false,
      rolloverBudget: flex.rolloverBudget ?? false,
      prorated: flex.prorated ?? false,
      gas: flex.activeOptions.gas,
      parking: flex.activeOptions.parking,
      transit: flex.activeOptions.transit,
      tolls: flex.activeOptions.tolls,
      rideshare: flex.activeOptions.rideshare,
      carshare: flex.activeOptions.carshare,
      transportationServices: flex.activeOptions.transportationServices,
      merchants: merchants,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'flex',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        budget: flex.budget,
        flexProviders: createMerchantsName(merchants, merchantsMap),
        gas: flex.activeOptions.gas,
        parking: flex.activeOptions.parking,
        transit: flex.activeOptions.transit,
        tolls: flex.activeOptions.tolls,
        rideshare: flex.activeOptions.rideshare,
        carshare: flex.activeOptions.carshare,
        transportationServices: flex.activeOptions.transportationServices,
      },
      segment.id,
      'flex',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'flex',
        id: segment.id,
      })
    )
  }
}

function* createFlexProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateFlexProgramForASegment,
    action,
    flexService.createFlexProgram,
    flexActions.createFlexProgramSuccess,
    'activate'
  )
}

function* updateFlexProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateFlexProgramForASegment,
    action,
    flexService.updateFlexProgram,
    flexActions.updateFlexProgramSuccess,
    'update'
  )
}

function* cancelFlexProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(flexService.cancelFlexProgram, programId)
    yield programCancelFlow(
      response,
      flexActions.cancelFlexProgramSuccess,
      programId,
      'flex'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'flex',
        id: programId,
      })
    )
  }
}

/*
########################################################
###################### TRANSIT PASS #####################
########################################################
*/
function* createUpdateTransitPassProgramForASegment(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    transitPass,
    selectedDate,
    segment,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'transit_pass',
      type: 'TRANSIT_PASS',
      budget: transitPass.walletTopUpEnabled ? transitPass.budget : 0,
      budgetCycle: 'MONTHLY',
      budgetRequired: false,
      purchaseType: transitPass.purchaseType,
      walletTopUpEnabled: transitPass.walletTopUpEnabled,
      passDeliveryMechanism: transitPass.passDeliveryMechanism,
      transitAgencies: transitPass.agenciesEnabled
        ? transitPass.transitAgencyIds
        : [],
      // Spend matching uses selected agencies' network_ids (see set_merchant_data)
      transit: false,
      gas: false,
      parking: false,
      tolls: false,
      rideshare: false,
      carshare: false,
      transportationServices: false,
      merchants: [],
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'transitPass',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        budget: transitPass.budget,
        purchaseType: transitPass.purchaseType,
        walletTopUpEnabled: transitPass.walletTopUpEnabled,
        passDeliveryMechanism: transitPass.passDeliveryMechanism,
        agenciesEnabled: transitPass.agenciesEnabled,
        transitAgencyIds: transitPass.agenciesEnabled
          ? transitPass.transitAgencyIds
          : [],
      },
      segment.id,
      'transitPass',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'transitPass',
        id: segment.id,
      })
    )
  }
}

function* createTransitPassProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateTransitPassProgramForASegment,
    action,
    flexService.createFlexProgram,
    transitPassActions.createTransitPassProgramSuccess,
    'activate'
  )
}

function* updateTransitPassProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateTransitPassProgramForASegment,
    action,
    flexService.updateFlexProgram,
    transitPassActions.updateTransitPassProgramSuccess,
    'update'
  )
}

function* cancelTransitPassProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(flexService.cancelFlexProgram, programId)
    yield programCancelFlow(
      response,
      transitPassActions.cancelTransitPassProgramSuccess,
      programId,
      'transitPass'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'transitPass',
        id: programId,
      })
    )
  }
}

/*
########################################################
###################### COUPON ##########################
########################################################
*/
function* createUpdateCouponProgramForASegment(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    coupon,
    selectedDate,
    segment,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'coupon',
      type: 'COUPON',
      budget: 0,
      budgetCycle: 'MONTHLY',
      budgetRequired: false,
      merchants: coupon.merchantsEnabled ? coupon.merchantIds : [],
      couponCode: coupon.couponCode?.trim() || null,
      transit: false,
      gas: false,
      parking: false,
      tolls: false,
      rideshare: false,
      carshare: false,
      transportationServices: false,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'coupon',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
        merchantsEnabled: coupon.merchantsEnabled,
        merchantIds: coupon.merchantsEnabled ? coupon.merchantIds : [],
        couponCode: coupon.couponCode,
      },
      segment.id,
      'coupon',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'coupon',
        id: segment.id,
      })
    )
  }
}

function* createCouponProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateCouponProgramForASegment,
    action,
    flexService.createFlexProgram,
    programSuccessActions.createCouponProgramSuccess,
    'activate'
  )
}

function* updateCouponProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateCouponProgramForASegment,
    action,
    flexService.updateFlexProgram,
    programSuccessActions.updateCouponProgramSuccess,
    'update'
  )
}

function* cancelCouponProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(flexService.cancelFlexProgram, programId)
    yield programCancelFlow(
      response,
      programSuccessActions.cancelCouponProgramSuccess,
      programId,
      'coupon'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'coupon',
        id: programId,
      })
    )
  }
}

function* createUpdateReportedUsageProgramForASegment(
  action: any,
  createUpdateService: any,
  successAction: ActionCreator<any>,
  actionType: 'activate' | 'update'
) {
  const {
    reportedUsage,
    selectedDate,
    segment,
    launchId,
    programId,
    superProgramName,
    description,
    currency,
  } = action.payload
  try {
    const response = yield call(createUpdateService, {
      startDate: moment(selectedDate.iso).format(),
      endDate: null,
      ...getProgramScopePayload(segment),
      name: 'reported usage',
      type: 'REPORTED_USAGE',
      budget: 0,
      budgetCycle: 'MONTHLY',
      budgetRequired: false,
      reportedMerchants: reportedUsage.merchants,
      merchants: [],
      transit: false,
      gas: false,
      parking: false,
      tolls: false,
      rideshare: false,
      carshare: false,
      transportationServices: false,
      ...(launchId && { launchId }),
      ...(superProgramName && { superProgramName }),
      ...(description?.trim() && { description: description.trim() }),
      ...(programId && { programId }),
      ...(currency && { currency }),
    })

    yield ProgramSuccessfulFlow(
      response,
      {
        action: actionType,
        programType: 'reportedUsage',
        segmentName: segment.text,
        startDate: moment(selectedDate.text, 'MMMM Do YYYY').format(),
      },
      segment.id,
      'reportedUsage',
      successAction
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'reportedUsage',
        id: segment.id,
      })
    )
  }
}

function* createReportedUsageProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateReportedUsageProgramForASegment,
    action,
    flexService.createFlexProgram,
    programSuccessActions.createReportedUsageProgramSuccess,
    'activate'
  )
}

function* updateReportedUsageProgramForASegmentSaga(action: any) {
  yield call(
    createUpdateReportedUsageProgramForASegment,
    action,
    flexService.updateFlexProgram,
    programSuccessActions.updateReportedUsageProgramSuccess,
    'update'
  )
}

function* cancelReportedUsageProgramForASegmentSaga(action: any) {
  const { programId } = action.payload
  try {
    const response = yield call(flexService.cancelFlexProgram, programId)
    yield programCancelFlow(
      response,
      programSuccessActions.cancelReportedUsageProgramSuccess,
      programId,
      'reportedUsage'
    )
  } catch (error) {
    yield put(
      programSuccessReducers.addProgramEditError({
        type: 'reportedUsage',
        id: programId,
      })
    )
  }
}

export function* programSuccessSaga() {
  yield takeLatest(
    programSuccessActions.newEditCycleCleanup.type,
    newEditCycleCleanupSaga
  )
  yield takeEvery(
    programSuccessActions.createQtfProgramForASegment.type,
    createQtfProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createBulkQtfProgramForASegmentList.type,
    createBulkQtfProgramsSaga
  )
  yield takeEvery(
    programSuccessActions.updateQtfProgramForASegment.type,
    updateQtfProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createGrhProgramForASegment.type,
    createGrhProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createMicromobilityProgramForASegment.type,
    createMicromobilityProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createFlexProgramForASegment.type,
    createFlexProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateFlexProgramForASegment.type,
    updateFlexProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createTransitPassProgramForASegment.type,
    createTransitPassProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateTransitPassProgramForASegment.type,
    updateTransitPassProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createCouponProgramForASegment.type,
    createCouponProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateCouponProgramForASegment.type,
    updateCouponProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateGrhProgramForASegment.type,
    updateGrhProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateMicromobilityProgramForASegment.type,
    updateMicromobilityProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelGrhProgramForASegment.type,
    cancelGrhProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelMicromobilityProgramForASegment.type,
    cancelMicromobilityProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelFlexProgramForASegment.type,
    cancelFlexProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelTransitPassProgramForASegment.type,
    cancelTransitPassProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelCouponProgramForASegment.type,
    cancelCouponProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.createReportedUsageProgramForASegment.type,
    createReportedUsageProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.updateReportedUsageProgramForASegment.type,
    updateReportedUsageProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelReportedUsageProgramForASegment.type,
    cancelReportedUsageProgramForASegmentSaga
  )
  yield takeEvery(
    programSuccessActions.cancelQtfProgramForASegment.type,
    cancelQtfProgramForASegmentSaga
  )
}

export default programSuccessSaga
